// src/pages/auth/CompleteProviderServicePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  FileText,
  MapPin,
  Clock,
  Languages,
  Calendar,
  Phone,
  Mail,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Info
} from 'lucide-react';

import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors'; 
import styles from '../../styles/CompleteProvider.module.css';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';

// Import from providerSlice
import {
  createProviderProfile,
  selectProviderCreating,
  selectProviderCreateError,
  selectProviderCreateSuccess,
  resetCreateState
} from '../../store/slices/providerSlice';

// Import from authSlice
import { selectUser,updateUser } from '../../store/slices/authSlice';
import { getProfile } from '../../store/slices/profileSlice';

const CompleteProviderServicePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get user from auth slice
  const user = useSelector(selectUser);
  const locationState = location.state || {};

  // Get provider slice states
  const isLoading = useSelector(selectProviderCreating);
  const providerError = useSelector(selectProviderCreateError);
  const providerSuccess = useSelector(selectProviderCreateSuccess);

  const [currentStep, setCurrentStep] = useState(1);
  const [hasShownSuccess, setHasShownSuccess] = useState(false);

  // Form data based on ServiceProvider model
  const [formData, setFormData] = useState({
    // Basic Info (from profile)
    userId: user?._id || locationState.userId || '',
    name: user?.name || locationState.name || '',
    phone: user?.phone || locationState.phone || '',
    email: user?.email || locationState.email || '',
    language: user?.language || locationState.language || 'ht',

    // Service Details
    serviceType: '',
    customServiceType: '',
    businessName: '',
    description: '',

    // Location
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Haiti' // Default, but user can change it
    },
    location: {
      coordinates: [0, 0] // [longitude, latitude]
    },

    // Experience
    yearsOfExperience: '',

    // Availability
    availability: {
      monday: { start: '09:00', end: '17:00', available: false },
      tuesday: { start: '09:00', end: '17:00', available: false },
      wednesday: { start: '09:00', end: '17:00', available: false },
      thursday: { start: '09:00', end: '17:00', available: false },
      friday: { start: '09:00', end: '17:00', available: false },
      saturday: { start: '09:00', end: '17:00', available: false },
      sunday: { start: '09:00', end: '17:00', available: false }
    },

    // Languages spoken
    languages: [],

    // Contact preferences
    contactPreferences: {
      phoneCalls: true,
      whatsapp: true,
      email: false
    },

    // Social Media
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    }
  });

  // Modal states
  const [serviceTypeModalVisible, setServiceTypeModalVisible] = useState(false);
  const [availabilityModalVisible, setAvailabilityModalVisible] = useState(false);
  const [languageModalVisible, setLanguageModalVisible] = useState(false);

  // Service types
  const serviceTypes = [
     'Electrician', 
    'Plumber', 
    'Mason', 
    'Painter', 
    'Carpenter', 
    'Tiler', 
    'Flooring Specialist',
    'Handyman',
    'HVAC Technician',
    'Solar Installer',
    
    // Beauty & Personal Care
    'Barber', 
    'Hairdresser', 
    'Makeup Artist',
    'Nail Technician',
    'Spa & Massage Therapist',
    
    // Automotive
    'Mechanic', 
    'Auto Body Repair',
    'Car Wash & Detailing',
    
    // Home Services
    'Interior Designer', 
    'Landscaper', 
    'Cleaner', 
    'Pest Control',
    'Pool Maintenance',
    'Locksmith',
    'Appliance Repair',
    
    // Food & Beverage Services
    'Restaurant',
    'Catering',
    'Food Delivery',
    'Food Truck',
    'Bakery',
    'Pastry Shop',
    'Coffee Shop',
    'Juice Bar',
    'Ice Cream Parlor',
    'Food Caterer',
    'Meal Prep Service',
    'Culinary Classes',
    
    // Other Services
    'Photographer',
    'Videographer',
    'Event Planner',
    'Wedding Planner',
    'Party Rentals',
    'DJ & Entertainment',
    'Tutor',
    'Language Instructor',
    'Fitness Trainer',
    'Yoga Instructor',
    'Childcare',
    'Elder Care',
    'Pet Grooming',
    'Pet Sitting',
    'Laundry Service',
    'Tailor',
    'Shoe Repair',
    'Jewelry Repair',
    'IT Support',
    'Web Developer',
    'Graphic Designer',
    'Digital Marketing',
    'Consultant',
    'Notary Public',
    'Translation Services',
    'Tour Guide',
    'Transportation',
    'Delivery Service',
    'Moving Company',
    'Storage Services',
    'Other'
  ];

  // Language options
  const languageOptions = [
    'Creole', 'French', 'English', 'Spanish', 'Other'
  ];

  // Days of week
  const daysOfWeek = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  const dayLabels = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  // Translations
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: fillAllFields } = useTranslation('Please fill in all required fields');
  const { translatedText: selectServiceType } = useTranslation('Please select your service type');
  const { translatedText: enterCity } = useTranslation('Please enter your city');
  const { translatedText: validExperience } = useTranslation('Please enter your years of experience');

  const { translatedText: profileCreated } = useTranslation('Profile Created!');
  const { translatedText: profileCreatedMessage } = useTranslation('Your service provider profile has been created successfully. You can now start offering your services.');
  const { translatedText: goToDashboard } = useTranslation('Go to Dashboard');
  const { translatedText: profileCreationFailed } = useTranslation('Profile Creation Failed');
  const { translatedText: tryAgain } = useTranslation('Please try again');

  const { translatedText: businessNameLabel } = useTranslation('Business Name');
  const { translatedText: businessNamePlaceholder } = useTranslation('Enter your business name');
  const { translatedText: serviceTypeLabel } = useTranslation('Service Type');
  const { translatedText: customServiceTypeLabel } = useTranslation('Custom Service Type');
  const { translatedText: customServiceTypePlaceholder } = useTranslation('Enter your service type');
  const { translatedText: yearsExperienceLabel } = useTranslation('Years Experience');
  const { translatedText: yearsExperiencePlaceholder } = useTranslation('e.g., 5');
  const { translatedText: descriptionLabel } = useTranslation('Description');
  const { translatedText: descriptionPlaceholder } = useTranslation('Describe your services, expertise, and what makes you unique...');

  const { translatedText: addressLabel } = useTranslation('Address');
  const { translatedText: streetPlaceholder } = useTranslation('Street address');
  const { translatedText: cityLabel } = useTranslation('City');
  const { translatedText: cityPlaceholder } = useTranslation('Enter your city');
  const { translatedText: stateLabel } = useTranslation('State/Province');
  const { translatedText: statePlaceholder } = useTranslation('Enter state/province');
  const { translatedText: zipCodeLabel } = useTranslation('Zip Code');
  const { translatedText: zipCodePlaceholder } = useTranslation('Enter zip code');
  const { translatedText: countryLabel } = useTranslation('Country');

  const { translatedText: languagesLabel } = useTranslation('Languages Spoken');
  const { translatedText: availabilityLabel } = useTranslation('Availability');
  const { translatedText: contactPreferencesLabel } = useTranslation('Contact Preferences');
  const { translatedText: socialMediaLabel } = useTranslation('Social Media');

  const { translatedText: facebookPlaceholder } = useTranslation('Facebook URL');
  const { translatedText: instagramPlaceholder } = useTranslation('Instagram URL');
  const { translatedText: twitterPlaceholder } = useTranslation('Twitter URL');

  const { translatedText: nextButton } = useTranslation('Next');
  const { translatedText: backButton } = useTranslation('Back');
  const { translatedText: createProfile } = useTranslation('Create Profile');
  const { translatedText: step1Of2 } = useTranslation('Step 1 of 2: Service Details');
  const { translatedText: step2Of2 } = useTranslation('Step 2 of 2: Location & Additional Info');
  const { translatedText: completeProfile } = useTranslation('Complete Your Profile');

  // Reset create state on mount and unmount
  useEffect(() => {
    dispatch(resetCreateState());
    setHasShownSuccess(false);

    return () => {
      dispatch(resetCreateState());
    };
  }, [dispatch]);

  // Handle provider success - only show alert once
  useEffect(() => {
    if (providerSuccess && !hasShownSuccess) {
      setHasShownSuccess(true);

      alert(
        `${profileCreated}\n\n${profileCreatedMessage}`
      );

      navigate('/provider-dashboard', { replace: true });
      dispatch(resetCreateState());
    }
  }, [providerSuccess, hasShownSuccess, profileCreated, profileCreatedMessage, navigate, dispatch]);

  // Handle provider error
  useEffect(() => {
    if (providerError) {
      alert(`${profileCreationFailed}\n\n${providerError}`);
    }
  }, [providerError, profileCreationFailed]);

  const validateStep1 = () => {
    if (!formData.serviceType) {
      alert(`${errorTitle}\n\n${selectServiceType}`);
      return false;
    }
    if (formData.serviceType === 'Other' && !formData.customServiceType) {
      alert(`${errorTitle}\n\nPlease specify your custom service type`);
      return false;
    }
    if (!formData.businessName) {
      alert(`${errorTitle}\n\nPlease enter your business name`);
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.address.city) {
      alert(`${errorTitle}\n\n${enterCity}`);
      return false;
    }
    if (!formData.yearsOfExperience || isNaN(parseInt(formData.yearsOfExperience))) {
      alert(`${errorTitle}\n\n${validExperience}`);
      return false;
    }
    if (formData.languages.length === 0) {
      alert(`${errorTitle}\n\nPlease select at least one language you speak`);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePlaceSelected = (locationData) => {
    if (locationData) {
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street: locationData.address || '',
          city: locationData.city || prev.address.city,
          state: locationData.state || prev.address.state,
          zipCode: locationData.zipCode || prev.address.zipCode,
          country: locationData.country || prev.address.country
        },
        location: {
          coordinates: [locationData.longitude || 0, locationData.latitude || 0]
        }
      }));
    }
  };

 const handleCreateProfile = async () => {
    if (!validateStep2()) return;

    const languageDisplayNames = formData.languages;

    const providerData = {
      userId: formData.userId,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      serviceType: formData.serviceType === 'Other' ? formData.customServiceType : formData.serviceType,
      businessName: formData.businessName,
      description: formData.description,
      address: {
        street: formData.address.street || '',
        city: formData.address.city,
        state: formData.address.state || '',
        zipCode: formData.address.zipCode || '',
        country: formData.address.country || 'Haiti'
      },
      location: {
        type: 'Point',
        coordinates: formData.location.coordinates
      },
      yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
      availability: formData.availability,
      languages: languageDisplayNames,
      contactPreferences: formData.contactPreferences,
      socialMedia: formData.socialMedia,
      language: formData.language || 'ht'
    };

    try {
      const result = await dispatch(createProviderProfile(providerData)).unwrap();
      
      // ✅ CRITICAL: Update the auth slice with provider data
      dispatch(updateUser({
        providerProfile: result.data,
        isProvider: true,
        role: 'service_provider'
      }));

      // ✅ Also update localStorage directly to ensure persistence
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          const updatedUserData = {
            ...userData,
            providerProfile: result.data,
            isProvider: true,
            role: 'service_provider'
          };
          localStorage.setItem('user_data', JSON.stringify(updatedUserData));
          console.log('✅ User data updated with provider profile');
        }
      } catch (error) {
        console.error('❌ Failed to update localStorage:', error);
      }

      // ✅ IMPORTANT: Wait for profile to fully load before navigating
      await dispatch(getProfile()).unwrap();
      
      // ✅ Now navigate to the next step - Profile Images
      console.log('✅ Provider profile created, redirecting to profile images...');
      navigate('/profile-image-provider', { replace: true });

    } catch (error) {
      console.error('Create provider error:', error);
    }
  };

  const toggleLanguage = (language) => {
    setFormData(prev => {
      const currentLanguages = prev.languages;
      const index = currentLanguages.indexOf(language);
      let newLanguages;
      if (index > -1) {
        newLanguages = currentLanguages.filter(l => l !== language);
      } else {
        newLanguages = [...currentLanguages, language];
      }
      return { ...prev, languages: newLanguages };
    });
  };

  const toggleDayAvailability = (day) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          available: !prev.availability[day].available
        }
      }
    }));
  };

  const updateAvailabilityTime = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  const toggleContactPreference = (method) => {
    setFormData(prev => ({
      ...prev,
      contactPreferences: {
        ...prev.contactPreferences,
        [method]: !prev.contactPreferences[method]
      }
    }));
  };

  const renderStep1 = () => (
    <>
      <div className={styles.inputGroup}>
        <label className={styles.label}>{serviceTypeLabel} *</label>
        <div
          className={styles.selectWrapper}
          onClick={() => setServiceTypeModalVisible(true)}
        >
          <Briefcase size={20} className={styles.inputIcon} color={colors.gray400} />
          <span className={!formData.serviceType ? styles.placeholderText : ''}>
            {formData.serviceType || `Select ${serviceTypeLabel}`}
          </span>
          <ChevronDown size={20} color={colors.gray400} className={styles.selectArrow} />
        </div>
      </div>

      {formData.serviceType === 'Other' && (
        <div className={styles.inputGroup}>
          <label className={styles.label}>{customServiceTypeLabel} *</label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.formControl}
              placeholder={customServiceTypePlaceholder}
              value={formData.customServiceType}
              onChange={(e) => setFormData({ ...formData, customServiceType: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className={styles.inputGroup}>
        <label className={styles.label}>{businessNameLabel} *</label>
        <div className={styles.inputWrapper}>
          <Building2 size={20} className={styles.inputIcon} color={colors.gray400} />
          <input
            type="text"
            className={styles.formControl}
            placeholder={businessNamePlaceholder}
            value={formData.businessName}
            onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
          />
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{descriptionLabel}</label>
        <div className={styles.inputWrapper}>
          <FileText size={20} className={`${styles.inputIcon} ${styles.textareaIcon}`} color={colors.gray400} />
          <textarea
            className={`${styles.formControl} ${styles.textarea}`}
            placeholder={descriptionPlaceholder}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
          />
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className={styles.inputGroup}>
        <label className={styles.label}>{addressLabel}</label>
        <div className={styles.infoBanner}>
          <Info size={20} color={colors.secondary} />
          <p className={styles.infoText}>Search for your location worldwide</p>
        </div>
      </div>

      {/* Google Places Autocomplete */}
      <div className={styles.inputGroup}>
        <PlacesAutocomplete
          onPlaceSelected={handlePlaceSelected}
          placeholder="Search for your business location..."
          style={{ marginBottom: '16px' }}
        />
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{streetPlaceholder}</label>
        <div className={styles.inputWrapper}>
          <MapPin size={20} className={styles.inputIcon} color={colors.gray400} />
          <input
            type="text"
            className={styles.formControl}
            placeholder={streetPlaceholder}
            value={formData.address.street}
            onChange={(e) => setFormData({
              ...formData,
              address: { ...formData.address, street: e.target.value }
            })}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.col6}>
          <label className={styles.label}>{cityLabel} *</label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.formControl}
              placeholder={cityPlaceholder}
              value={formData.address.city}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, city: e.target.value }
              })}
            />
          </div>
        </div>
        <div className={styles.col6}>
          <label className={styles.label}>{stateLabel}</label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.formControl}
              placeholder={statePlaceholder}
              value={formData.address.state}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, state: e.target.value }
              })}
            />
          </div>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.col6}>
          <label className={styles.label}>{zipCodeLabel}</label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.formControl}
              placeholder={zipCodePlaceholder}
              value={formData.address.zipCode}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, zipCode: e.target.value }
              })}
            />
          </div>
        </div>
        <div className={styles.col6}>
          <label className={styles.label}>{countryLabel}</label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.formControl}
              placeholder="Enter country"
              value={formData.address.country}
              onChange={(e) => setFormData({
                ...formData,
                address: { ...formData.address, country: e.target.value }
              })}
            />
          </div>
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{yearsExperienceLabel} *</label>
        <div className={styles.inputWrapper}>
          <Clock size={20} className={styles.inputIcon} color={colors.gray400} />
          <input
            type="number"
            className={styles.formControl}
            placeholder={yearsExperiencePlaceholder}
            value={formData.yearsOfExperience}
            onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
            min="0"
            max="99"
          />
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{languagesLabel} *</label>
        <div className={styles.languageGrid}>
          {languageOptions.map((lang) => (
            <button
              key={lang}
              type="button"
              className={`${styles.languageOption} ${formData.languages.includes(lang) ? styles.languageOptionActive : ''}`}
              onClick={() => toggleLanguage(lang)}
            >
              {formData.languages.includes(lang) && <Check size={16} />}
              {lang}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{availabilityLabel}</label>
        <div
          className={styles.selectWrapper}
          onClick={() => setAvailabilityModalVisible(true)}
        >
          <Calendar size={20} className={styles.inputIcon} color={colors.gray400} />
          <span>
            {Object.keys(formData.availability).filter(day => formData.availability[day].available).length} days available
          </span>
          <ChevronRight size={20} color={colors.gray400} className={styles.selectArrow} />
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{contactPreferencesLabel}</label>
        <div className={styles.contactPreferenceContainer}>
          {['phoneCalls', 'whatsapp', 'email'].map((method) => (
            <button
              key={method}
              type="button"
              className={`${styles.contactPreferenceOption} ${formData.contactPreferences[method] ? styles.contactPreferenceOptionActive : ''}`}
              onClick={() => toggleContactPreference(method)}
            >
              {method === 'phoneCalls' && <Phone size={20} />}
              {method === 'whatsapp' && <span className={styles.whatsappIcon}>💬</span>}
              {method === 'email' && <Mail size={20} />}
              <span>{method === 'phoneCalls' ? 'Phone' : method === 'whatsapp' ? 'WhatsApp' : 'Email'}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.label}>{socialMediaLabel}</label>
        <div className={styles.inputWrapper}>
          <img 
            src="https://s.magecdn.com/social/tc-facebook.svg" 
            alt="Facebook" 
            width="20" 
            height="20" 
            className={styles.socialIcon}
          />
          <input
            type="text"
            className={styles.formControl}
            placeholder={facebookPlaceholder}
            value={formData.socialMedia.facebook}
            onChange={(e) => setFormData({
              ...formData,
              socialMedia: { ...formData.socialMedia, facebook: e.target.value }
            })}
          />
        </div>
        <div className={`${styles.inputWrapper} ${styles.mt8}`}>
          <img 
            src="https://s.magecdn.com/social/tc-instagram.svg" 
            alt="Instagram" 
            width="20" 
            height="20" 
            className={styles.socialIcon}
          />
          <input
            type="text"
            className={styles.formControl}
            placeholder={instagramPlaceholder}
            value={formData.socialMedia.instagram}
            onChange={(e) => setFormData({
              ...formData,
              socialMedia: { ...formData.socialMedia, instagram: e.target.value }
            })}
          />
        </div>
        <div className={`${styles.inputWrapper} ${styles.mt8}`}>
          <img 
            src="https://s.magecdn.com/social/tc-twitter.svg" 
            alt="Twitter" 
            width="20" 
            height="20" 
            className={styles.socialIcon}
          />
          <input
            type="text"
            className={styles.formControl}
            placeholder={twitterPlaceholder}
            value={formData.socialMedia.twitter}
            onChange={(e) => setFormData({
              ...formData,
              socialMedia: { ...formData.socialMedia, twitter: e.target.value }
            })}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowLeft size={24} color={colors.textPrimary} />
          </button>
          <div className={styles.headerContent}>
            <h2 className={styles.headerTitle}>{completeProfile}</h2>
            <div className={styles.stepIndicator}>
              <div className={`${styles.stepDot} ${currentStep === 1 ? styles.stepDotActive : ''}`} />
              <div className={`${styles.stepLine} ${currentStep === 1 ? styles.stepLineActive : ''}`} />
              <div className={`${styles.stepDot} ${currentStep === 2 ? styles.stepDotActive : ''}`} />
            </div>
            <small className={styles.headerSubtitle}>
              {currentStep === 1 ? step1Of2 : step2Of2}
            </small>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className={styles.formContainer}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}

            <div className={styles.buttonRow}>
              {currentStep > 1 && (
                <button
                  type="button"
                  className={styles.backButtonSecondary}
                  onClick={handleBack}
                >
                  <ArrowLeft size={20} />
                  <span>{backButton}</span>
                </button>
              )}

              <button
                type="button"
                className={`${styles.nextButton} ${currentStep === 2 ? styles.registerButton : ''}`}
                onClick={currentStep === 2 ? handleCreateProfile : handleNext}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 size={20} className={styles.spinner} />
                ) : (
                  <>
                    <span>{currentStep === 2 ? createProfile : nextButton}</span>
                    {currentStep === 1 && <ChevronRight size={20} />}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Service Type Modal */}
        {serviceTypeModalVisible && (
          <div className={styles.modalOverlay} onClick={() => setServiceTypeModalVisible(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{serviceTypeLabel}</h3>
                <button
                  className={styles.modalClose}
                  onClick={() => setServiceTypeModalVisible(false)}
                >
                  ✕
                </button>
              </div>
              <div className={styles.modalList}>
                {serviceTypes.map((type) => (
                  <button
                    key={type}
                    className={`${styles.modalItem} ${formData.serviceType === type ? styles.modalItemActive : ''}`}
                    onClick={() => {
                      setFormData({ ...formData, serviceType: type });
                      setServiceTypeModalVisible(false);
                    }}
                  >
                    <span>{type}</span>
                    {formData.serviceType === type && <Check size={20} color={colors.primary} />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Availability Modal */}
        {availabilityModalVisible && (
          <div className={styles.modalOverlay} onClick={() => setAvailabilityModalVisible(false)}>
            <div className={`${styles.modalContent} ${styles.availabilityModal}`} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{availabilityLabel}</h3>
                <button
                  className={styles.modalClose}
                  onClick={() => setAvailabilityModalVisible(false)}
                >
                  ✕
                </button>
              </div>
              <div className={styles.availabilityList}>
                {daysOfWeek.map((day) => (
                  <div key={day} className={styles.availabilityDayContainer}>
                    <div className={styles.availabilityDayHeader}>
                      <span className={styles.availabilityDayLabel}>{dayLabels[day]}</span>
                      <label className={styles.switch}>
                        <input
                          type="checkbox"
                          checked={formData.availability[day].available}
                          onChange={() => toggleDayAvailability(day)}
                        />
                        <span className={styles.slider} />
                      </label>
                    </div>
                    {formData.availability[day].available && (
                      <div className={styles.availabilityTimeRow}>
                        <div className={styles.availabilityTimeInput}>
                          <label>Start</label>
                          <input
                            type="time"
                            value={formData.availability[day].start}
                            onChange={(e) => updateAvailabilityTime(day, 'start', e.target.value)}
                          />
                        </div>
                        <div className={styles.availabilityTimeInput}>
                          <label>End</label>
                          <input
                            type="time"
                            value={formData.availability[day].end}
                            onChange={(e) => updateAvailabilityTime(day, 'end', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button
                className={styles.modalDoneButton}
                onClick={() => setAvailabilityModalVisible(false)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompleteProviderServicePage;